import { AuthCallback, RequireAuth, RequireNoAuth, RequireVerification } from '@psyomics/ui-common';
import {
  BreadcrumbRouteConfig,
  ConfirmForgotPassword,
  ForgotPassword,
  GenericError,
  RequireCookies,
  BackgroundLayout,
  BackgroundRouteConfig,
} from '@psyomics/ui-components';
import { Route, Routes } from 'react-router-dom';

import { ContentIndex } from '../features/content/index/content-index';
import { HeaderIllustrationLayout } from '../shared/components/header-illustration-layout/header-illustration-layout';
import { InsecureLayout } from '../shared/components/insecure-layout/insecure-layout';
import { MobileLayout } from '../shared/components/mobile-layout/mobile-layout';
import { SecureLayout } from '../shared/components/secure-layout/secure-layout';

import { PATIENT_REDIRECT_WHITELIST } from './redirect-whitelists';
import { About } from './routes/about/about';
import { Account } from './routes/account/account';
import { ChangePassword } from './routes/account/change-password';
import { ChangePasswordSuccess } from './routes/account/change-password-success';
import { ContentPage } from './routes/content/content-page';
import { ForgotPasswordSuccess } from './routes/forgot-password/success';
import { Home } from './routes/home/home';
import { MfaSetup } from './routes/mfa-setup/mfa-setup';
import { RequireMfaSetup } from './routes/mfa-setup/require-mfa-setup';
import Questionnaire from './routes/questionnaire/questionnaire';
import { Referral } from './routes/referral/referral';
import { SelfReferral, SelfReferralInit } from './routes/self-referral/self-referral';
import { SelfReferralRedirect } from './routes/self-referral/self-referral-redirect';
import { SmsOptout } from './routes/sms-opt-out/sms-opt-out';
import { SmsOptOutSuccess } from './routes/sms-opt-out-success/sms-opt-out-success';
import { AccountVerification } from './routes/verification/account-verification';
import { SelfHostedLogin } from './self-hosted-login';
import { SelfHostedRegister } from './self-hosted-register';

const getIndexRoute = () => '/';
const getLoginRoute = () => '/login';
const forgotPasswordRoute = '/forgot-password';
const resolveVerificationMethod = (hasEmail: boolean | null) => (hasEmail ? 'email' : 'phone');

const unauthenticatedRoutes = (
  <Route Component={InsecureLayout}>
    <Route Component={SmsOptout} path="/sms-opt-out" />
    <Route Component={SmsOptOutSuccess} path="/sms-opt-out-success" />
    <Route Component={RequireCookies}>
      <Route Component={MobileLayout}>
        <Route Component={Referral} path="/referral/:invitePublicId/*" />
        <Route Component={SelfHostedRegister} path="/register" />
        <Route Component={ForgotPassword} path={forgotPasswordRoute} />
        <Route Component={ConfirmForgotPassword} path={`${forgotPasswordRoute}/confirm`} />
        <Route Component={ForgotPasswordSuccess} path={`${forgotPasswordRoute}/success`} />
      </Route>
      <Route Component={HeaderIllustrationLayout}>
        <Route Component={MobileLayout}>
          <Route element={<SelfHostedLogin forgotPasswordUrl={forgotPasswordRoute} />} path="/login" />
        </Route>
      </Route>
    </Route>
    <Route element={<GenericError titleKey="common.errors.notFound" />} path="/*"></Route>
  </Route>
);

const authenticatedRoutes = (
  <Route
    element={
      <RequireVerification redirectWhitelist={PATIENT_REDIRECT_WHITELIST} resolveMethod={resolveVerificationMethod} />
    }
  >
    <Route Component={SecureLayout}>
      <Route Component={MobileLayout}>
        <Route Component={AccountVerification} path="account-verification" />
      </Route>
      <Route element={<RequireMfaSetup />}>
        <Route element={<BreadcrumbRouteConfig crumb={{ labelKey: 'navigation.home', href: '/' }} />}>
          <Route element={<MobileLayout width="full" />}>
            <Route Component={Questionnaire} path="/questionnaire/:id" />
            <Route element={<Questionnaire reviewMode />} path="/questionnaire/:id/review" />
          </Route>
          <Route Component={MobileLayout}>
            <Route element={<BackgroundRouteConfig variant="purpleGreen" />}>
              <Route Component={Home} index />
            </Route>
            <Route path="/content">
              <Route
                element={<BreadcrumbRouteConfig crumb={{ labelKey: 'page.healthLibrary.title', href: '/content' }} />}
              >
                <Route element={<BackgroundRouteConfig variant="purpleGreen" />}>
                  <Route Component={ContentIndex} index />
                </Route>
                <Route element={<BackgroundRouteConfig opacity="0.25" variant="indigoPurple" />}>
                  <Route Component={ContentPage} path=":type/:id" />
                </Route>
              </Route>
            </Route>
            <Route Component={MfaSetup} path="mfa-setup" />
            <Route Component={SelfReferralInit} path="/self-referral/init/:selfReferralCode" />
            <Route Component={Account} path="account" />
            <Route Component={ChangePassword} path="account/change-password" />
            <Route Component={ChangePasswordSuccess} path="account/change-password-success" />
            <Route Component={About} path="about" />
            <Route element={<GenericError titleKey="common.errors.notFound" />} path="/*"></Route>
          </Route>
        </Route>
      </Route>
    </Route>
  </Route>
);

const AuthRoutes = () => (
  <Routes>
    <Route Component={BackgroundLayout}>
      <Route Component={AuthCallback} path="/auth/callback" />
      <Route Component={SelfReferralRedirect} path="/self-referral/redirect" />
      <Route Component={InsecureLayout}>
        <Route Component={RequireCookies}>
          <Route Component={SelfReferral} path="/self-referral/:selfReferralCode" />
        </Route>
      </Route>
      <Route element={<RequireNoAuth getIndexRoute={getIndexRoute} />}>{unauthenticatedRoutes}</Route>
      <Route element={<RequireAuth getLoginRoute={getLoginRoute} />}>{authenticatedRoutes}</Route>
    </Route>
  </Routes>
);

export { AuthRoutes, resolveVerificationMethod };
